import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import {Check as CheckIcon} from "@mui/icons-material";
import OpcoesEmCartao from "../../Metricas/components/OpcoesEmCartao";
import {
    FONTES,
    FORMAS,
    NATUREZAS,
    apontamentoVazio,
    fontesPermitidas,
    formDoApontamento,
    recusaDaFonte,
    rotulo,
    unidadeDeCobranca,
    validarApontamento,
    versaoPublicada,
    versoesNoSistema
} from "../../Metricas/metricasRegras";
import {getBackendEntityMessage} from "../sistemasUtils";
import {getVersaoDoPlano} from "../SistemasService";

// As versões publicadas deste sistema que bloqueiam ou têm teto nesta métrica: sem bloqueio no sistema, o limite vira
// só aviso nelas. O GET da métrica diz quais versões usam; o limite vem da versão.
const useConflitoDoBloqueio = (aberto, metrica, sistemaId, bloqueia) => {
    const [conflito, setConflito] = useState([]);
    useEffect(() => {
        let ativo = true;
        const publicadas = versoesNoSistema(metrica, sistemaId).filter(versaoPublicada);
        if (!aberto || bloqueia || !publicadas.length) {
            setConflito([]);
            return undefined;
        }
        Promise.allSettled(publicadas.map((versao) => getVersaoDoPlano(versao.id_plano, versao.numero))).then((respostas) => {
            if (!ativo) return;
            setConflito(publicadas.filter((versao, i) => {
                const completa = respostas[i].status === 'fulfilled' ? (respostas[i].value?.data?.valor ?? respostas[i].value?.data) : null;
                const limite = (completa?.limites || []).find((item) => item.id_metrica === metrica.id);
                return limite && ((limite.regra || limite.ao_atingir || metrica.ao_atingir) === 'BLOQUEAR' || Number(limite.teto) > 0);
            }));
        });
        return () => {
            ativo = false;
        };
    }, [aberto, bloqueia, metrica, sistemaId]);
    return conflito;
};

/*
 * Apontar uma métrica no sistema, ou editar o apontamento (protótipo v2, modal "apontar"): de onde vem o número, como o
 * sistema mede, se ele consegue bloquear e o custo de referência, usado só na margem do simulador. A fonte segue a
 * regra da API: só usuários vem do backoffice, consumo chega por eventos e estado pela foto diária.
 */
export default function ApontarMetricaDialog({aberto, sistema, catalogo = [], editando, onSalvar, onFechar}) {
    const [form, setForm] = useState(apontamentoVazio);
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!aberto) return;
        setForm(editando ? formDoApontamento(editando) : apontamentoVazio());
        setErro('');
        setSalvando(false);
    }, [aberto, editando]);

    const apontadas = new Set((sistema?.metricas || []).map((item) => item.metrica_id));
    const livres = catalogo.filter((metrica) => !apontadas.has(metrica.id));
    const metrica = catalogo.find((item) => item.id === form.metrica_id) || null;
    const conflito = useConflitoDoBloqueio(aberto, metrica, sistema?.id, form.bloqueia);

    // Ao escolher a métrica, a fonte vai para a única que a regra aceita, quando é uma só.
    const escolherMetrica = (metricaId) => {
        const escolhida = catalogo.find((item) => item.id === metricaId);
        const permitidas = fontesPermitidas(escolhida);
        setForm((atual) => ({
            ...atual,
            metrica_id: metricaId,
            fonte: permitidas.includes(atual.fonte) ? atual.fonte : (permitidas.length === 1 ? permitidas[0] : ''),
        }));
        setErro('');
    };

    const salvar = async () => {
        const problema = validarApontamento(form, metrica);
        if (problema) {
            setErro(problema);
            return;
        }
        setSalvando(true);
        setErro('');
        try {
            await onSalvar(form, metrica);
        } catch (error) {
            setErro(getBackendEntityMessage(error, 'Não foi possível salvar o apontamento.'));
            setSalvando(false);
        }
    };

    const titulo = editando ? `Editar ${metrica?.nome || editando.nome || 'métrica'} em ${sistema?.nome}` : `Apontar métrica em ${sistema?.nome}`;

    return (
        <Dialog open={aberto} onClose={salvando ? undefined : onFechar} fullWidth maxWidth="md">
            <DialogTitle>{titulo}</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{pt: 0.5}}>
                    {editando ? (
                        <Box sx={{px: 1.5, py: 1, borderLeft: 3, borderColor: 'primary.main', bgcolor: 'action.hover'}}>
                            <Typography variant="caption" color="text.secondary" display="block">Métrica</Typography>
                            <Typography variant="body2" fontWeight={700} component="span">{metrica?.nome || editando.nome}</Typography>
                            {' '}<Typography variant="caption" color="text.secondary" component="span" sx={{fontFamily: 'monospace'}}>{metrica?.chave || editando.chave}</Typography>
                        </Box>
                    ) : livres.length ? (
                        <OpcoesEmCartao
                            rotulo="Métrica"
                            valor={form.metrica_id}
                            colunas={3}
                            opcoes={livres.map((item) => ({
                                value: item.id,
                                titulo: item.nome,
                                texto: `${rotulo(NATUREZAS, item.natureza)} · ${rotulo(FORMAS, item.forma_cobranca).toLowerCase()}`,
                            }))}
                            onEscolher={escolherMetrica}
                        />
                    ) : (
                        <Alert severity="info" variant="outlined">Todas as métricas do cadastro já estão apontadas neste sistema.</Alert>
                    )}

                    <OpcoesEmCartao
                        rotulo="Fonte da medição"
                        valor={form.fonte}
                        colunas={3}
                        opcoes={Object.entries(FONTES).map(([value, item]) => {
                            const recusa = metrica ? recusaDaFonte(metrica, value) : '';
                            return {value, titulo: item.label, texto: item.texto, disabled: !metrica || Boolean(recusa), motivo: recusa || 'Escolha a métrica antes.'};
                        })}
                        onEscolher={(fonte) => setForm((atual) => ({...atual, fonte}))}
                    />

                    <TextField
                        label="Como mede"
                        value={form.como_mede}
                        onChange={(event) => setForm((atual) => ({...atual, como_mede: event.target.value}))}
                        placeholder="O que conta, de onde vem e quando é enviado"
                        multiline
                        minRows={2}
                        fullWidth
                    />

                    <Box sx={{display: 'grid', gap: 2.5, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, alignItems: 'start'}}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">Bloqueio</Typography>
                            <FormControlLabel
                                control={<Switch checked={form.bloqueia} onChange={(event) => setForm((atual) => ({...atual, bloqueia: event.target.checked}))} />}
                                label="O sistema consegue bloquear ao atingir o limite"
                            />
                        </Box>
                        <TextField
                            label="Custo de referência (opcional)"
                            type="number"
                            value={form.custo_referencia}
                            onChange={(event) => setForm((atual) => ({...atual, custo_referencia: event.target.value}))}
                            helperText="Usado só para a margem no simulador."
                            inputProps={{min: 0, step: '0.01'}}
                            InputProps={{endAdornment: <InputAdornment position="end">R$ por {metrica ? unidadeDeCobranca(metrica) : 'unidade de cobrança'}</InputAdornment>}}
                        />
                    </Box>

                    {conflito.length ? (
                        <Alert severity="warning" variant="outlined">
                            Sem bloqueio no sistema, o limite vira só aviso em {conflito.map((versao) => `${versao.nome_plano} v${versao.numero}`).join(', ')}, que usam bloqueio ou teto.
                        </Alert>
                    ) : null}
                    {erro ? <Alert severity="error">{erro}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onFechar} disabled={salvando}>Cancelar</Button>
                <Button variant="contained" onClick={salvar} disabled={salvando || (!editando && !livres.length)} startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}>
                    {editando ? 'Salvar' : 'Apontar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
